import { useState, useEffect, FC, useMemo, useRef } from "react";
import { Select, MenuItem, FormControl, InputLabel, Box, useMediaQuery, Typography, Button, IconButton, Tooltip } from "@mui/material";
import { DataGridPremium, GridSortModel } from "@mui/x-data-grid-premium";
import { PREMIUM_FEATURES_OFF } from "@/components/data-grid/premium";
// The matrix uses the Pro grid like the rest of the app. `pagination` keeps the footer and 100-row pages the free grid
// gave it by default, and the three disable* props switch off what Pro turns on and the free grid never had (column
// resize and reorder, multi-column sort), so nothing changes on screen (#1285 item 12).
import Module from "@/components/module/Module";
import { useAtomValue } from "jotai";
import { darkerBackgroundColorState, lighterBackgroundColorState, textColorState, themeState } from "@/lib/store";
import { getStockOptionsMatrix } from "@/controllers/stock-options";
import { Stock } from "@/models/stock";
import { nFormatter } from "@/lib/utils";
import Skeleton from "@/components/skeleton/Skeleton";
import styles from "./StockOptionsMatrix.module.scss";
import FeedbackForm from "@/components/feedback-form/FeedbackForm";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";

// Interface defining the structure of option data

interface OptionData {
    oi: number;
    volume: number;
    lastPrice: number;
    lastSize: number;
    askPrice: number;
    askSize: number;
    bidPrice: number | null;
    bidSize: number | null;
    impliedVol: number | null;
    delta: number | null;
    gamma: number | null;
    theta: number | null;
    vega: number | null;
    rho: number | null;
}
// Functional component that represents the Option Matrix

const OptionMatrix: FC<{ stock: Stock }> = ({ stock }) => {
    // Determine if the screen size is desktop or not
    const isDesktop = useMediaQuery("(min-width:1280px)");
    const is4k = useMediaQuery("(min-width:2560px)");
    // State variables
    const [expiries, setExpiries] = useState<any>(isDesktop ? 10 : 5);
    const [dataPoint, setDataPoint] = useState("oi");
    const [data, setData] = useState({ current: undefined, past: undefined });
    const [diffOption, setDiffOption] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);
    const isRatioData = ["putCallOiRatio", "putCallVolumeRatio"].includes(dataPoint);
    const [sortByCall, setSortByCall] = useState(false);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "strike",
            sort: "asc",
        },
    ]);
    const [showAllData, setShowAllData] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState(false);

    const handleSetSortModel = (model: GridSortModel) => {
        setSortModel(model);
    };

    // Reference to the container div
    const containerRef = useRef<HTMLDivElement>(null);

    // Options for day difference select
    const diffOptions = [
        { label: "No difference", value: 0 },
        { label: "1 Day Change", value: 1 },
        { label: "3 Day Change", value: 3 },
        { label: "1 Week Change", value: 7 },
        { label: "1 Month Change", value: 30 },
    ];

    // Fetch option matrix data when component mounts or diffOption changes
    useEffect(() => {
        setIsLoading(true);
        if (diffOption === 0) {
            getStockOptionsMatrix(stock.tradingitem_id, null, showAllData).then((res) => {
                setData({ current: res, past: undefined });
                setIsLoading(false);
            });
        } else {
            getStockOptionsMatrix(stock.tradingitem_id, diffOption, showAllData).then((res) => {
                setData((data) => ({ current: data.current, past: res }));
                setIsLoading(false);
            });
        }
    }, [stock, diffOption, showAllData]);

    // Adjust number of expirations displayed when screen size changes
    useEffect(() => {
        setExpiries(is4k ? 20 : isDesktop ? 10 : 5);
    }, [isDesktop, is4k]);

    const formatPercentageValue = (num) => parseFloat(num.toFixed(2)).toString();

    // Function to calculate differences between current and past data
    const calculateDifferences = () => {
        const differences = {};
        Object.keys(data.current).forEach((expiry) => {
            differences[expiry] = {};
            Object.keys(data.current[expiry]).forEach((strike) => {
                let putDiff: number, putPercentage: number;
                let callDiff: number, callPercentage: number;
                const cellCurrent = data.current[expiry][strike];
                const cellPast = data.past[expiry]?.[strike];
                if (!isRatioData) {
                    let currentCallValue = cellCurrent?.call?.[dataPoint] || 0;
                    let pastCallValue = cellPast?.call?.[dataPoint] || 0;

                    let currentPutValue = cellCurrent ? cellCurrent.put?.[dataPoint] : 0;
                    let pastPutValue = cellPast ? cellPast.put?.[dataPoint] : 0;
                    callDiff = currentCallValue - pastCallValue;
                    putDiff = currentPutValue - pastPutValue;
                    callPercentage = pastCallValue !== 0 ? (callDiff / pastCallValue) * 100 : 0;
                    putPercentage = pastPutValue !== 0 ? (putDiff / pastPutValue) * 100 : 0;

                    differences[expiry][strike] = {
                        call: { diff: callDiff, percentage: callPercentage ? formatPercentageValue(callPercentage) : 0 },
                        put: { diff: putDiff, percentage: putPercentage ? formatPercentageValue(putPercentage) : 0 },
                    };
                } else {
                    let currentValue = cellCurrent?.[dataPoint] || 0;
                    let pastValue = cellPast?.[dataPoint] || 0;
                    const diff = pastValue !== 0 && pastValue !== currentValue ? pastValue - currentValue : 0;
                    const percentage = diff === 0 ? 0 : (diff * 100) / pastValue;

                    differences[expiry][strike] = {
                        diff: nFormatter(diff, 2),
                        percentage: percentage ? formatPercentageValue(percentage) : 0,
                    };
                }
            });
        });
        return differences;
    };

    // Memoized computation of differences
    const differences = useMemo(() => {
        if (!data || !data.current || !data.past) return {};
        return calculateDifferences();
    }, [data, dataPoint, diffOption]);

    // Datapoints options for datapoint select
    const dataPoints = [
        { key: "putCallOiRatio", label: "Put Call Ratio (OI)" },
        { key: "putCallVolumeRatio", label: "Put Call Ratio (Volume)" },
        { key: "oi", label: "Open Interest" },
        { key: "volume", label: "Volume" },
        { key: "lastPrice", label: "Price" },
        { key: "impliedVol", label: "Implied Volatility" },
        { key: "delta", label: "Delta" },
        { key: "gamma", label: "Gamma" },
        { key: "theta", label: "Theta" },
        { key: "vega", label: "Vega" },
        { key: "rho", label: "Rho" },
    ];

    // Sorted expiry list (same as columns) so row data and heatmap scale match what we display
    const sortedExpiryKeys = useMemo(
        () =>
            data.current
                ? Object.keys(data.current)
                      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                      .slice(0, expiries)
                : [],
        [data.current, expiries]
    );

    // Min and Max value used to get transparency/opacity values (only from displayed expiries)
    const allValues = useMemo(() => {
        if (!data.current || sortedExpiryKeys.length === 0) return [];
        return sortedExpiryKeys
            .flatMap((expiry) =>
                Object.entries(data.current[expiry] || {}).flatMap(([strike, strikeData]: [string, any]) => {
                    if (isRatioData) {
                        if (data.past !== undefined) {
                            let currentValue = data.current[expiry]?.[strike]?.[dataPoint] || 0;
                            let pastValue = data.past[expiry]?.[strike]?.[dataPoint] || 0;
                            const diff = pastValue !== 0 && pastValue !== currentValue ? pastValue - currentValue : 0;
                            return Math.abs(diff);
                        } else {
                            const ratioValue = Number(strikeData[dataPoint]);
                            return isNaN(ratioValue) ? null : Math.abs(ratioValue);
                        }
                    } else {
                        const callValue = Number(strikeData?.call?.[dataPoint]);
                        const putValue = Number(strikeData?.put?.[dataPoint]);
                        return [isNaN(callValue) ? null : callValue, isNaN(putValue) ? null : putValue];
                    }
                })
            )
            .filter((val) => val !== null);
    }, [data, dataPoint, isRatioData, sortedExpiryKeys]);

    const minValue = useMemo(() => (allValues.length > 0 ? Math.min(...allValues) : 0), [allValues]);
    const maxValue = useMemo(() => (allValues.length > 0 ? Math.max(...allValues) : 1), [allValues]);

    // function to get the transparency/opacity for a certain cell
    const getTransparency = (value: number, min: number, max: number) => {
        if (value === undefined || value === null) return 0;
        if (min === max) return 0.5;
        return 0.1 + 0.9 * ((value - min) / (max - min));
    };

    const columnsArray = sortedExpiryKeys;

    const rows =
        data.current && columnsArray.length > 0
            ? Object.keys(data.current[columnsArray[0]]).map((strike, index) => {
                  let row: any = { id: index, strike };
                  columnsArray.forEach((expiry) => {
                      const cell = data.current[expiry][strike];
                      if (isRatioData) {
                          const value = cell !== undefined ? cell[dataPoint] : 0;
                          row[expiry] = value;
                      } else {
                          const call = cell !== undefined ? cell.call?.[dataPoint] : 0;
                          const put = cell !== undefined ? cell.put?.[dataPoint] : 0;
                          row[expiry] = { call, put };
                      }
                  });
                  return row;
              })
            : [];

    // Columns
    const columns = useMemo(() => {
        let containerWidth = containerRef.current?.offsetWidth - 20 || 0;
        let dividedWidth = columnsArray?.length > 1 ? Math.floor(containerWidth / (columnsArray?.length + 1)) : 250;

        let width = isDesktop ? Math.max(dividedWidth, diffOption === 0 ? 150 : 250) : 250;

        return [
            {
                field: "strike",
                headerName: "Strike",
                width: isDesktop && diffOption === 0 ? Math.max(dividedWidth, 100) : 100,
                sortComparator: (v1: string, v2: string) => {
                    return parseFloat(v1) - parseFloat(v2);
                },
            },
            ...columnsArray.map((expiry) => ({
                field: expiry,
                width,
                renderHeader: () => (
                    <Box style={{ width, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
                        <Typography sx={{ width, display: "flex", alignItems: "center", justifyContent: "center" }}>{expiry}</Typography>
                        {!isRatioData && (
                            <Box sx={{ height: 25, width: width - 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography
                                    onClick={() => setSortByCall(true)}
                                    sx={{
                                        cursor: "pointer",
                                        height: 25,
                                        width: width / 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRight: "2px solid",
                                        borderColor: theme === "light" ? darkerBackgroundColor : lighterBackgroundColor,
                                        backgroundColor: sortByCall ? "rgba(49, 171, 166, 0.2)" : "transparent",
                                    }}
                                >
                                    Call
                                </Typography>
                                <Typography
                                    onClick={() => setSortByCall(false)}
                                    sx={{
                                        cursor: "pointer",
                                        height: 25,
                                        width: width / 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: !sortByCall ? "rgba(49, 171, 166, 0.2)" : "transparent",
                                    }}
                                >
                                    Put
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ),
                sortComparator: (v1: any, v2: any) => {
                    if (isRatioData) {
                        return parseFloat(v1) - parseFloat(v2);
                    } else {
                        return sortByCall ? (v1.call || 0) - (v2.call || 0) : (v1.put || 0) - (v2.put || 0);
                    }
                },
                valueGetter: (params: any) => params.row[expiry] || 0,
                renderCell: (params: any) => {
                    if (data.past !== undefined) {
                        if (isRatioData) {
                            const ratioDiff = differences[params.field]?.[params.row.strike] || {};
                            const transparency = getTransparency(Math.abs(ratioDiff.diff), 0, maxValue);
                            return (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", maxHeight: "100%", height: "50px", width: "100%", backgroundColor: `rgba(49, 171, 166, ${transparency})` }}>
                                    {ratioDiff.diff !== undefined ? (
                                        <>
                                            {ratioDiff.percentage}% ( {ratioDiff.diff} )
                                        </>
                                    ) : (
                                        "N/A"
                                    )}
                                </Box>
                            );
                        } else {
                            const callDiff = differences[params.field]?.[params.row.strike]?.call || {};
                            const putDiff = differences[params.field]?.[params.row.strike]?.put || {};
                            const callTransparency = getTransparency(callDiff.percentage, minValue, maxValue);
                            const putTransparency = getTransparency(putDiff.percentage, minValue, maxValue);
                            return (
                                <Box key={`${expiry}-${params.row.strike}`} sx={{ display: "flex", maxHeight: "100%", height: "50px", width: "100%" }}>
                                    <Box
                                        sx={{
                                            width: "50%",
                                            backgroundColor: `rgba(49, 171, 166, ${callTransparency})`,
                                            padding: 1,
                                            borderLeft: "2px solid",
                                            borderRight: "2px solid",
                                            borderColor: theme === "light" ? darkerBackgroundColor : lighterBackgroundColor,
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {callDiff.diff !== undefined ? (
                                            <>
                                                {callDiff?.percentage}% ( {nFormatter(callDiff?.diff, 2)} )
                                            </>
                                        ) : (
                                            "N/A"
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            width: "50%",
                                            backgroundColor: `rgba(49, 171, 166, ${putTransparency})`,
                                            padding: 1,
                                            borderRight: "2px solid",
                                            borderColor: theme === "light" ? darkerBackgroundColor : lighterBackgroundColor,
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {putDiff.diff !== undefined ? (
                                            <>
                                                {putDiff?.percentage}% ( {nFormatter(putDiff?.diff, 2)} )
                                            </>
                                        ) : (
                                            "N/A"
                                        )}
                                    </Box>
                                </Box>
                            );
                        }
                    } else {
                        if (isRatioData) {
                            const transparency = getTransparency(Math.abs(params.value), minValue, maxValue);
                            return (
                                <Box sx={{ width: "100%", maxHeight: "100%", height: "50px", backgroundColor: `rgba(49, 171, 166, ${transparency})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {nFormatter(params.value, 2)}
                                </Box>
                            );
                        } else {
                            const { call, put } = params.row[expiry] || {};
                            const callTransparency = getTransparency(call, minValue, maxValue);
                            const putTransparency = getTransparency(put, minValue, maxValue);
                            return (
                                <Box key={`${expiry}-${params.row.strike}`} sx={{ display: "flex", maxHeight: "100%", height: "50px", width: "100%" }}>
                                    <Box
                                        sx={{
                                            width: "50%",
                                            backgroundColor: `rgba(49, 171, 166, ${callTransparency})`,
                                            padding: 1,
                                            borderRight: "2px solid",
                                            borderColor: theme === "light" ? darkerBackgroundColor : lighterBackgroundColor,
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {nFormatter(call, 2)}
                                    </Box>
                                    <Box sx={{ width: "50%", backgroundColor: `rgba(49, 171, 166, ${putTransparency})`, display: "flex", alignItems: "center", justifyContent: "center", padding: 1 }}>{nFormatter(put, 2)}</Box>
                                </Box>
                            );
                        }
                    }
                },
            })),
        ];
    }, [expiries, columnsArray, containerRef.current, data, dataPoint, diffOption, sortByCall]);

    return (
        <Module noHeader>
            <Box className={styles.optionsContainer}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", maxHeight: "55px" }}>
                    <FormControl style={{ minWidth: "150px" }} variant="outlined">
                        <InputLabel id="expiries-select-label">Expiries</InputLabel>
                        <Select labelId="expiries-select-label" label="Expiries" value={expiries} onChange={(e) => setExpiries(e.target.value)} size="small" style={{ backgroundColor: "transparent" }}>
                            {[5, 10, 20].map((num) => (
                                <MenuItem key={num} value={num}>{`${num} Expiries`}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined">
                        <InputLabel id="data-point-select-label">Data Point</InputLabel>
                        <Select labelId="data-point-select-label" label="Data Point" value={dataPoint} onChange={(e) => setDataPoint(e.target.value)} size="small" style={{ backgroundColor: "transparent" }}>
                            {dataPoints.map((point) => (
                                <MenuItem key={point.key} value={point.key}>
                                    {point.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined">
                        <InputLabel id="diff-option-select-label">Difference Option</InputLabel>
                        <Select
                            labelId="diff-option-select-label"
                            label="Difference Option"
                            value={diffOption}
                            onChange={(e) => setDiffOption(typeof e.target.value === "string" ? parseInt(e.target.value) : e.target.value)}
                            size="small"
                            style={{ backgroundColor: "transparent", width: "min-content" }}
                        >
                            {diffOptions.map((option) => (
                                <MenuItem key={option.label} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box>
                        <Tooltip title="Send feedback" arrow>
                            <IconButton aria-label="send feedback" className={styles.button} onClick={() => setFeedbackModal(true)} sx={{ mt: 0.3 }}>
                                <FeedbackOutlinedIcon style={{ color: textColor }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Button variant={showAllData ? "outlined" : "contained"} onClick={() => setShowAllData(!showAllData)} size="medium" disabled={isLoading}>
                        {showAllData ? "Show Less Data" : "Show All Data"}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ height: "calc(100% - 65px)", display: "flex", alignItems: "center", justifyContent: "center" }} ref={containerRef}>
                <style>
                    {`
                .MuiSvgIcon-root {
                    fill: ${textColor} !important;
                    color: ${textColor} !important;
                }
                    
                .MuiDataGrid-columnHeader:hover .MuiDataGrid-iconButtonContainer {
                    display: none;
                }
                
                .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer {
                display: flex !important;
                }

                 .MuiDataGrid-iconButtonContainer {
                    position: ${sortModel?.[0]?.field === "strike" ? "relative" : "absolute"};
                    top: 0;
                    right: 0;
                }

                
            `}
                </style>
                {isLoading ? (
                    <Skeleton width="100%" height="100%" variant="rounded" loading={isLoading} />
                ) : (
                    <DataGridPremium
                        {...PREMIUM_FEATURES_OFF}
                        pagination
                        rows={rows}
                        columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSetSortModel}
                        disableRowSelectionOnClick
                        disableColumnFilter
                        disableColumnMenu
                        disableColumnResize
                        disableColumnReorder
                        disableMultipleColumnsSorting
                    />
                )}
                <FeedbackForm isOpen={feedbackModal} onClose={setFeedbackModal} moduleId={"stock_fundamentals"} />
            </Box>
        </Module>
    );
};

export default OptionMatrix;
